/**
 * Uplifted pieces of `utils/apiService` (apps/web + apps/mobile).
 *
 * This folder MIRRORS the app file. `shared.ts` holds the parts that are byte-identical in
 * both apps and touch neither the redux store nor any platform API:
 * `getErrorTextFromResponse` and `FORCED_LOGOUT_ERROR_CODES`.
 *
 * ✅ **The axios instance and its two interceptors now live in `index.desktop.ts` /
 * `index.native.ts`** (2026-08-20). They are HEAVILY divergent, so there is no shared body to
 * factor: this file holds only the two pieces that are genuinely identical on both platforms.
 * Both blockers the earlier version of this comment described were settled by the two bridges —
 * the store reads/dispatches go through core's own store, and web's `ToastNotifyError` (UI, which
 * core must never import) through `effects-bridge.notifyError`.
 *
 * **What each side has that the other does not** — this table lives HERE, not in either platform
 * file, because a platform file's comments end up in that platform's bundle and a comment naming
 * the other platform's identifiers reads as a resolution leak to a symbol-level bundle grep
 * (ledger hazard 16):
 *
 * | web only | mobile only |
 * |---|---|
 * | the `config.offlineFn` short-circuit | `addRouteToStore` / `ENDPOINT_SLICE_MAP` |
 * | the recommendation-engine base URL | `excludedErrorCodes` toast suppression |
 * | the `RESET_ATOKEN` and `?atoken=` fallbacks | the Sentry `auth_session_expiry` event |
 * | the `AbortSignal` offline gate | `setTimeout(… setLoader(false), 400)` |
 * | `structuredClone` of the request-header map | the no-cache default headers |
 * | `lastNetworkError` moment-based throttling | |
 * | `REST_VERSION_MISMATCH` → adhoc table refetch | |
 * | a request-interceptor rejection handler | |
 * | `saveRequestHeaderData` on the response | |
 */

/**
 * The `If-Modified-Since` value to send when `requestHeaderStore.data` has NO entry for a URL —
 * i.e. "I have nothing cached, give me a 200 with the body". A date so old the backend's
 * comparison can never be satisfied, so it always answers 200.
 *
 * 🔴 **This constant exists because `= null` stopped working when the web app moved to axios 1.x.**
 * Commit `9fa77ad19` (2026-08-20) removed `axios ^0.27.2` from `apps/web/package.json` and gave
 * `@rista/core` `axios ^1.19.0`; with `nodeLinker: hoisted` the web bundle resolves that single
 * hoisted copy, so the app took a MAJOR axios bump as a side effect of a module move. (The
 * `pnpm.overrides.axios` in the root `package.json` is NOT the mechanism — pnpm 11 ignores that
 * field entirely; see UPLIFT_LEDGER hazard 33.) It is not cosmetic — it is the whole freshness
 * contract:
 *
 * - axios **0.27** (what apps/web ran on release_4.0.0) kept `config.headers` as a plain object
 *   and handed it straight to `setRequestHeader`, so `= null` went on the wire as the literal
 *   string `"null"`. The backend could not parse it and replied 200 + body — the intended
 *   "no condition" behaviour.
 * - axios **1.x** wraps headers in `AxiosHeaders` and `toJSON()` DROPS null values
 *   (`value != null && value !== false && ...`, `lib/core/AxiosHeaders.js`), so the header is
 *   omitted entirely.
 *
 * Omitting it is not equivalent, and that is the trap: without a caller-supplied conditional
 * header the request becomes an ordinary cacheable GET, and since these responses carry
 * `Last-Modified` with no `Cache-Control` they are *heuristically cacheable*. The BROWSER then
 * takes over — it either revalidates on its own (304, and serves its stale cached body to axios
 * as a 200) or serves the cached body without touching the network at all. Both were measured in
 * Chrome; both look like "the API returned stale data" or "the data never arrived", and both
 * disappear the moment a conditional header is present, because Chrome then forwards the request
 * with `cache-control: max-age=0` instead of answering from its cache.
 *
 * So the sentinel has to be a real value, not an absent header. Keep it a parseable HTTP-date
 * rather than reviving `"null"`: it does not depend on the backend being lenient about garbage.
 *
 * ⚠️ Do NOT "simplify" this to `if (stored) config.headers[...] = stored` — dropping the else
 * branch is exactly the broken shape above.
 *
 * Mobile is unaffected today only because its axios instance sets
 * `Cache-Control: no-cache, no-store, must-revalidate` as a default header, which keeps the cache
 * out by a different route; it has been on axios 1.x all along.
 */
export const IF_MODIFIED_SINCE_NONE = "Thu, 01 Jan 1970 00:00:00 GMT";

/**
 * Turns a backend error payload into display text, walking the seven shapes the API is known
 * to return (plain string, nested message/reason, Xeno-style nested error, errorDescription,
 * then the bare code as a fallback).
 *
 * Byte-identical in both apps apart from quote style — verified by a normalised diff — so it
 * is shared rather than split. Note it is EXPORTED in both apps but imported by neither: the
 * only callers are each app's own response interceptor. The export is preserved because it is
 * public surface, not because anything consumes it today.
 */
/**
 * Response codes that force a logout. BYTE-IDENTICAL in both apps (each declared it as a local
 * `let errorCodes = [...]` inside its own response interceptor), so it is shared — what the two
 * platforms then DO with a match diverges completely and stays in the platform files:
 * web toasts the mapped message and calls `handleLogoutAction`; mobile logs an
 * `auth_session_expiry` Sentry event carrying the ATOKEN and calls `checkSupportLogout`.
 *
 * ⚠️ Mobile ALSO suppresses `REST_LICENCE_OPERATION_FAILED` from the generic error toast via its
 * own `excludedErrorCodes` list; that list is a different set and is deliberately not merged
 * into this one.
 */
export const FORCED_LOGOUT_ERROR_CODES = [
  "IDLE_TIMEOUT",
  "UNAUTHENTICATED_USER",
  "REST_LICENCE_OPERATION_FAILED",
  "REST_STALE_CLIENT_VERSION",
];

export const getErrorTextFromResponse = (data: any = {}) => {
  const { code, message } = data || {};
  let errorText;

  // 1. Use plain string message if backend returned message as a string
  if (typeof message === "string") {
    errorText = message;
  }
  // 2. Otherwise, use nested message.message or message.reason (common REST pattern)
  else if (message?.message || message?.reason) {
    errorText = message?.message || message?.reason;
  }
  // 3. Otherwise, if message.error is a string, use that directly
  else if (typeof message?.error === "string") {
    errorText = message?.error;
  }
  // 4. Otherwise, use message.error.message or message.error.reason (Xeno-style nested error)
  else if (message?.error?.message || message?.error?.reason) {
    errorText = message?.error?.message || message?.error?.reason;
  }
  // 5. Otherwise, use message.error.code as a last choice from nested error object
  else if (message?.error?.code) {
    errorText = message?.error?.code;
  }
  // 6. handle errorDescription from new API format if message is an object and has errorDescription field, use it as error text
  else if (message?.errorDescription) {
    errorText = message.errorDescription;
  }
  // 7. Fallback: if nothing else matched, use top-level error code
  else {
    errorText = code;
  }

  return errorText;
};

/**
 * `quietUi`: a v2 request (`ordering/commit/request.ts`) says its own outcome through v2's toast
 * and alert, so both interceptors skip THEIR toasts and the approval modal for it (decided
 * 2026-10-06). Everything else they do still runs. No v1 request sets the flag.
 */
export const isQuietRequest = (config: any): boolean => Boolean(config?.quietUi);
